import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowDownUp, Calendar, Plane, Repeat, Users } from "lucide-react";
import dayjs from "dayjs";

import { cn } from "../../lib/utils";
import {
  Extra,
  MAX_FLIGHT_LENGTH,
  MAX_NAME_LENGTH,
  MAX_NOTES_LENGTH,
  Place,
  PricingConfig,
  WithId,
} from "../../lib/pricing";
import { UseQuoteReturn } from "../../hooks/useQuote";
import PlaceCombobox from "./PlaceCombobox";
import PassengerStepper from "./PassengerStepper";
import ExtrasPicker from "./ExtrasPicker";
import { Tone, tones } from "./tones";

interface Props {
  quote: UseQuoteReturn;
  config: PricingConfig;
  places: WithId<Place>[];
  extras: WithId<Extra>[];
  zoneNames: Record<string, string>;
  zoneOrder: string[];
  tone?: Tone;
  /** oculta o card de contato (ex.: simulador do admin) */
  showContact?: boolean;
}

export default function QuoteForm({
  quote,
  config,
  places,
  extras,
  zoneNames,
  zoneOrder,
  tone = "light",
  showContact = true,
}: Props) {
  const { t } = useTranslation();
  const c = tones[tone];
  const { state, setField, setPassengers, swapEndpoints, toggleExtra, maxPassengers } = quote;

  const section = (step: number, title: string, children: React.ReactNode) => (
    <section className={cn(c.card, "space-y-4")}>
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
          {step}
        </span>
        <h2 className={c.title}>{title}</h2>
      </div>
      {children}
    </section>
  );

  const field = (label: string, control: React.ReactNode, hint?: string, htmlFor?: string) => (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className={c.label}>
        {label}
        {hint && <span className={cn("ml-2 font-normal normal-case tracking-normal", c.hint)}>{hint}</span>}
      </label>
      {control}
    </div>
  );

  const minDate = dayjs().format("YYYY-MM-DDTHH:mm");

  return (
    <div className="space-y-4">
      {section(
        1,
        t("quote.steps.route"),
        <>
          {field(
            t("quote.origin"),
            <PlaceCombobox
              id="quote-origin"
              tone={tone}
              value={state.origin}
              onChange={(value) => setField("origin", value)}
              places={places}
              zoneNames={zoneNames}
              zoneOrder={zoneOrder}
              placeholder={t("quote.originPlaceholder")}
            />,
            t("quote.optional"),
            "quote-origin"
          )}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={swapEndpoints}
              className={cn(
                "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition",
                c.chip
              )}
            >
              <ArrowDownUp className="h-3.5 w-3.5" /> {t("quote.swap")}
            </button>
          </div>
          {field(
            t("quote.destination"),
            <PlaceCombobox
              id="quote-destination"
              tone={tone}
              value={state.destination}
              onChange={(value) => setField("destination", value)}
              places={places}
              zoneNames={zoneNames}
              zoneOrder={zoneOrder}
              placeholder={t("quote.destinationPlaceholder")}
            />,
            undefined,
            "quote-destination"
          )}
          {config.roundTrip.enabled && (
            <div className={cn("inline-flex rounded-full border p-1", c.toggle)} role="radiogroup">
              {[
                { value: false, label: t("quote.oneWay") },
                { value: true, label: t("quote.roundTrip") },
              ].map((option) => {
                const active = state.roundTrip === option.value;
                return (
                  <button
                    key={String(option.value)}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setField("roundTrip", option.value)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition",
                      active ? c.toggleActive : c.toggleInactive
                    )}
                  >
                    {option.value && <Repeat className="h-3.5 w-3.5" />}
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {section(
        2,
        t("quote.steps.trip"),
        <>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="space-y-1">
              <p className={cn(c.label, "flex items-center gap-2")}>
                <Users className="h-4 w-4" /> {t("quote.passengers")}
              </p>
              <p className={c.hint}>{t("quote.maxPassengers", { count: maxPassengers })}</p>
            </div>
            <PassengerStepper
              tone={tone}
              value={state.passengers}
              max={maxPassengers}
              onChange={setPassengers}
              label={t("quote.passengers")}
            />
          </div>
          {state.passengers >= maxPassengers && (
            <p className={cn("rounded-xl px-3 py-2 text-xs", c.badgeWarn)}>{t("quote.morePassengers")}</p>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            {field(
              t("quote.date"),
              <div className="relative">
                <Calendar className={cn("pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2", c.muted)} />
                <input
                  id="quote-date"
                  type="datetime-local"
                  min={minDate}
                  value={state.date}
                  onChange={(e) => setField("date", e.target.value)}
                  className={cn(c.input, "min-w-0 pl-9")}
                />
              </div>,
              t("quote.optional"),
              "quote-date"
            )}
            {field(
              t("quote.flight"),
              <div className="relative">
                <Plane className={cn("pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2", c.muted)} />
                <input
                  id="quote-flight"
                  maxLength={MAX_FLIGHT_LENGTH}
                  value={state.flight}
                  onChange={(e) => setField("flight", e.target.value.toUpperCase())}
                  placeholder={t("quote.flightPlaceholder")}
                  className={cn(c.input, "min-w-0 pl-9 uppercase")}
                />
              </div>,
              t("quote.optional"),
              "quote-flight"
            )}
          </div>

          {extras.some((e) => e.active) &&
            field(
              t("quote.extras"),
              <ExtrasPicker tone={tone} extras={extras} selected={state.extraIds} onToggle={toggleExtra} />,
              t("quote.optional")
            )}
        </>
      )}

      {showContact &&
        section(
          3,
          t("quote.steps.contact"),
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              {field(
                t("quote.name"),
                <input
                  id="quote-name"
                  maxLength={MAX_NAME_LENGTH}
                  autoComplete="name"
                  value={state.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder={t("quote.namePlaceholder")}
                  className={c.input}
                />,
                t("quote.optional"),
                "quote-name"
              )}
              {field(
                t("quote.phone"),
                <input
                  id="quote-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  maxLength={20}
                  value={state.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  placeholder={t("quote.phonePlaceholder")}
                  className={c.input}
                />,
                t("quote.optional"),
                "quote-phone"
              )}
            </div>
            {field(
              t("quote.notes"),
              <textarea
                id="quote-notes"
                rows={3}
                maxLength={MAX_NOTES_LENGTH}
                value={state.notes}
                onChange={(e) => setField("notes", e.target.value)}
                placeholder={t("quote.notesPlaceholder")}
                className={cn(c.textarea, "resize-none")}
              />,
              t("quote.optional"),
              "quote-notes"
            )}
          </>
        )}
    </div>
  );
}
